import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageCircle, Send, Bot, User,
  Sparkles, Pill, Heart, Calendar, Lightbulb,
  ChevronRight, ChevronLeft, RotateCcw, HelpCircle, Square
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { getQuickInsights } from '@/services/patientWorkflowService';
import { useStreamChat } from '@/hooks/useStreamChat';
import ConversationHistory from '@/components/ConversationHistory';

const PATIENT_PROMPTS = [
  { icon: Pill, label: "What medications am I taking?", color: "text-emerald-500" },
  { icon: Heart, label: "Tell me about my conditions", color: "text-rose-500" },
  { icon: Calendar, label: "When is my next appointment?", color: "text-blue-500" },
  { icon: Lightbulb, label: "Give me health tips", color: "text-amber-500" },
  { icon: HelpCircle, label: "Explain my lab results", color: "text-clinical" },
];

const PatientChatSidebar = ({ patientId, patientName = "Patient" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [insights, setInsights] = useState([]);
  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  const {
    messages, conversationId, isStreaming,
    sendMessage: streamSend, stopStreaming, newConversation, loadConversation,
  } = useStreamChat(patientId, 'patient');

  // Rich HTML renderer for bot messages
  const renderMessageContent = useCallback((content) => {
    if (!content) return null;
    let html = content;

    // Escape HTML entities first (prevent XSS)
    html = html.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Headers: **Title** at start of line → styled heading
    html = html.replace(/^\*\*(.+?)\*\*$/gm, '<div class="font-semibold text-slate-900 dark:text-white mt-2 mb-1 text-[13px]">$1</div>');
    // Remaining bold
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold text-slate-900 dark:text-white">$1</strong>');

    // Success markers: ✅ or ✓
    html = html.replace(/^(✅|✓)\s*(.+)$/gm, '<div class="flex items-start gap-1.5 my-0.5"><span class="inline-flex items-center justify-center h-4 w-4 rounded-full bg-emerald-100 text-emerald-600 text-[10px] shrink-0 mt-0.5">✓</span><span class="text-emerald-700">$2</span></div>');

    // Warning markers: ⚠️
    html = html.replace(/^(⚠️)\s*(.+)$/gm, '<div class="flex items-start gap-1.5 my-0.5"><span class="inline-flex items-center justify-center h-4 w-4 rounded-full bg-amber-100 text-amber-600 text-[10px] shrink-0 mt-0.5">!</span><span class="text-amber-700">$2</span></div>');

    // Info markers: ℹ️ or 💡
    html = html.replace(/^(ℹ️|💡)\s*(.+)$/gm, '<div class="flex items-start gap-1.5 my-0.5 p-1.5 rounded bg-blue-50 border border-blue-100"><span class="text-sm shrink-0">$1</span><span class="text-blue-800 text-[12px]">$2</span></div>');

    // Pill/medication: 💊
    html = html.replace(/^(💊)\s*(.+)$/gm, '<div class="flex items-start gap-1.5 my-1 p-2 rounded-lg bg-clinical-soft border border-clinical/20"><span class="text-sm shrink-0">💊</span><span class="text-clinical text-[12px]">$2</span></div>');

    // Calendar/appointment: 📅
    html = html.replace(/^(📅)\s*(.+)$/gm, '<div class="flex items-start gap-1.5 my-1 p-2 rounded-lg bg-indigo-50 border border-indigo-100"><span class="text-sm shrink-0">📅</span><span class="text-indigo-800 text-[12px]">$2</span></div>');

    // Heart/condition: ❤️ or 🫀
    html = html.replace(/^(❤️|🫀)\s*(.+)$/gm, '<div class="flex items-start gap-1.5 my-1 p-2 rounded-lg bg-rose-50 border border-rose-100"><span class="text-sm shrink-0">$1</span><span class="text-rose-800 text-[12px]">$2</span></div>');

    // Other emoji section headers
    html = html.replace(/^([\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}])\s*(.+)$/gmu, '<div class="flex items-center gap-1.5 mt-2 mb-1 font-medium text-slate-700"><span class="text-sm">$1</span><span class="text-[12px]">$2</span></div>');

    // Bullet points
    html = html.replace(/^[•·]\s*(.+)$/gm, '<div class="flex items-start gap-2 my-0.5 ml-1"><span class="h-1.5 w-1.5 rounded-full bg-clinical mt-1.5 shrink-0"></span><span>$1</span></div>');
    html = html.replace(/^-\s+(.+)$/gm, '<div class="flex items-start gap-2 my-0.5 ml-1"><span class="h-1.5 w-1.5 rounded-full bg-clinical mt-1.5 shrink-0"></span><span>$1</span></div>');

    // Numbered lists
    html = html.replace(/^(\d+)\.\s+(.+)$/gm, '<div class="flex items-start gap-2 my-0.5 ml-1"><span class="inline-flex items-center justify-center h-5 w-5 rounded-full bg-clinical-soft text-clinical text-[10px] font-bold shrink-0">$1</span><span>$2</span></div>');

    // Internal links: [[Link Text|/path]] → clickable navigation
    html = html.replace(/\[\[(.+?)\|(.+?)\]\]/g, '<a data-navigate="$2" class="text-clinical hover:underline cursor-pointer font-medium">$1</a>');

    // Convert remaining newlines to line breaks
    html = html.replace(/\n/g, '<br/>');
    html = html.replace(/(<\/div>)<br\/>/g, '$1');

    return (
      <div
        className="text-[13px] leading-relaxed [&_strong]:font-semibold"
        dangerouslySetInnerHTML={{ __html: html }}
        onClick={(e) => {
          const navPath = e.target.getAttribute('data-navigate');
          if (navPath) {
            e.preventDefault();
            navigate(navPath);
          }
          const suggestion = e.target.getAttribute('data-suggestion');
          if (suggestion) {
            e.preventDefault();
            streamSend(suggestion);
          }
        }}
      />
    );
  }, [navigate, streamSend]);

  useEffect(() => {
    if (isOpen) setTimeout(() => inputRef.current?.focus(), 300);
  }, [isOpen]);

  useEffect(() => {
    const el = scrollRef.current?.querySelector('[data-radix-scroll-area-viewport]');
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (patientId && isOpen && insights.length === 0) {
      getQuickInsights(patientId).then(res => setInsights(res.insights || [])).catch(() => {});
    }
  }, [patientId, isOpen, insights.length]);

  const handleSend = (text) => {
    const msg = text || message.trim();
    if (!msg) return;
    streamSend(msg);
    setMessage('');
  };

  return (
    <>
      {/* Toggle Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            onClick={() => setIsOpen(true)}
            className="fixed right-0 top-1/2 z-40 flex -translate-y-1/2 items-center gap-2 rounded-l-lg border border-r-0 border-border/40 bg-primary py-3 pl-3 pr-2 text-primary-foreground shadow-[0_14px_40px_rgba(15,23,42,0.18)] transition-shadow hover:shadow-xl"
          >
            <MessageCircle className="h-5 w-5" />
            <span className="text-xs font-medium hidden sm:block">Chat</span>
            <ChevronLeft className="h-4 w-4" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Sidebar Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="emr-flyout-panel emr-flyout-rail fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col overflow-hidden sm:bottom-3 sm:right-3 sm:top-3 sm:w-[min(400px,calc(100vw-1.5rem))] sm:rounded-lg"
            >
              {/* Header */}
              <div className="emr-flyout-header flex items-center justify-between px-4 py-3 pl-5">
                <div className="flex items-center gap-2.5">
                  <div className="flex items-center justify-center w-9 h-9 rounded-full bg-white/20">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold">Health Assistant</h3>
                    <p className="text-[10px] text-primary-foreground/75">Ask me anything about your health</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <ConversationHistory
                    patientId={patientId}
                    role="patient"
                    onSelect={loadConversation}
                    onNewChat={newConversation}
                    activeConversationId={conversationId}
                  />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/10" onClick={newConversation}>
                    <RotateCcw className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-white/80 hover:text-white hover:bg-white/10" onClick={() => setIsOpen(false)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea ref={scrollRef} className="flex-1 px-4 py-3">
                {messages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-clinical-soft flex items-center justify-center mb-3">
                      <Bot className="h-7 w-7 text-clinical" />
                    </div>
                    <h4 className="text-sm font-semibold mb-1">Hello, {patientName}!</h4>
                    <p className="text-xs text-muted-foreground mb-5 max-w-xs">
                      I can help you understand your health records, medications, and appointments.
                    </p>

                    {insights.length > 0 && (
                      <div className="w-full space-y-2 mb-4">
                        {insights.map((insight, i) => (
                          <div key={i} className="flex items-start gap-2.5 px-3 py-2 rounded-lg bg-clinical-soft/50 border border-clinical/20 text-left">
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 mt-0.5 border-clinical/30 text-clinical">
                              {insight.type}
                            </Badge>
                            <span className="text-xs text-foreground">{insight.message}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="w-full space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Quick questions</p>
                      {PATIENT_PROMPTS.map((p, i) => (
                        <button
                          key={i}
                          onClick={() => handleSend(p.label)}
                          className="w-full flex items-center gap-2.5 text-left px-3 py-2.5 rounded-lg border hover:border-clinical/40 hover:bg-clinical-soft/50 transition-colors"
                        >
                          <p.icon className={`h-4 w-4 flex-shrink-0 ${p.color}`} />
                          <span className="text-xs text-muted-foreground">{p.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((msg, idx) => (
                      <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                        {msg.role === 'assistant' && (
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-7 h-7 rounded-full bg-clinical-soft flex items-center justify-center">
                              <Bot className="h-4 w-4 text-clinical" />
                            </div>
                          </div>
                        )}
                        <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm ${
                          msg.role === 'user'
                            ? 'bg-clinical text-white rounded-br-sm'
                            : msg.isError
                              ? 'bg-red-50 text-red-700 border border-red-200 rounded-bl-sm'
                              : 'bg-muted/60 text-foreground border border-border/50 rounded-bl-sm'
                        }`}>
                          <div className="leading-relaxed">
                            {msg.role === 'assistant' && !msg.isError
                              ? renderMessageContent(msg.content)
                              : <span className="whitespace-pre-wrap">{msg.content}</span>
                            }
                            {msg.streaming && <span className="ml-0.5 inline-block h-4 w-1.5 animate-pulse rounded-sm bg-clinical align-text-bottom" />}
                          </div>
                        </div>
                        {msg.role === 'user' && (
                          <div className="flex-shrink-0 mt-1">
                            <div className="w-7 h-7 rounded-full bg-clinical flex items-center justify-center">
                              <User className="h-4 w-4 text-white" />
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Input */}
              <div className="emr-flyout-footer px-3 py-3">
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }} className="flex gap-2">
                  <Input
                    ref={inputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Type your question..."
                    disabled={isStreaming}
                    className="text-sm rounded-full px-4"
                  />
                  {isStreaming ? (
                    <Button type="button" size="icon" onClick={stopStreaming} className="rounded-full bg-red-500 hover:bg-red-600 flex-shrink-0">
                      <Square className="h-3.5 w-3.5" />
                    </Button>
                  ) : (
                    <Button type="submit" size="icon" disabled={!message.trim()} className="rounded-full bg-clinical hover:bg-clinical/90 flex-shrink-0">
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                </form>
                <p className="text-[10px] text-center text-muted-foreground mt-1.5">
                  For medical emergencies, call emergency services immediately
                </p>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default PatientChatSidebar;
